// src/components/forms/variants/corner/CornerEmailCapture.tsx

'use client'

import { forwardRef, useState } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import RiCloseLine from '~/icons/remix/generated/RiCloseLine'
import RiMailLine from '~/icons/remix/generated/RiMailLine'
import { cn } from '~/lib/utils'
import { BaseEmailCaptureForm } from '~/components/forms/base/BaseEmailCaptureForm'
import { FormErrorBoundary } from '~/components/forms/base/ErrorBoundary'
import type { RenderProps } from '~/components/forms/base/types'
import type { CornerEmailCaptureProps } from '~/components/forms/variants/corner/types'
import { CORNER_PRESET } from '~/components/forms/variants/corner/types'
import { siteConfig } from '~/lib/config/site'

export const CornerEmailCapture = forwardRef<HTMLFormElement, CornerEmailCaptureProps>(({
  position = 'bottom-right',
  theme: customTheme,
  content: customContent = {},
  className,
  onSubmit,
  ...props
}, ref) => {
  const [isExpanded, setIsExpanded] = useState(false)

  const theme = {
    ...CORNER_PRESET.theme,
    ...customTheme,
    className: {
      ...CORNER_PRESET.theme?.className,
      ...customTheme?.className
    }
  }

  // Ensure required fields are present in content
  const content = {
    ...CORNER_PRESET.content,
    ...customContent,
    button: {
      text: CORNER_PRESET.content?.button?.text ?? 'Subscribe',
      loadingText: CORNER_PRESET.content?.button?.loadingText ?? 'Processing...',
      ...CORNER_PRESET.content?.button,
      ...customContent?.button
    },
    input: {
      placeholder: CORNER_PRESET.content?.input?.placeholder ?? 'Enter your email',
      ...CORNER_PRESET.content?.input,
      ...customContent?.input
    },
    messages: {
      success: CORNER_PRESET.content?.messages?.success ?? 'Thanks for subscribing!',
      error: CORNER_PRESET.content?.messages?.error ?? 'Please enter a valid email address.',
      ...CORNER_PRESET.content?.messages,
      ...customContent?.messages
    },
    privacy: {
      enabled: CORNER_PRESET.content?.privacy?.enabled ?? true,
      text: CORNER_PRESET.content?.privacy?.text ?? 'By continuing, you agree to our',
      ...CORNER_PRESET.content?.privacy,
      ...customContent?.privacy,
      privacyLink: {
        text: CORNER_PRESET.content?.privacy?.privacyLink?.text ?? 'Privacy Policy',
        url: CORNER_PRESET.content?.privacy?.privacyLink?.url ?? `${siteConfig.baseLinks.privacy}`,
        ...CORNER_PRESET.content?.privacy?.privacyLink,
        ...customContent?.privacy?.privacyLink
      },
      termsLink: {
        text: CORNER_PRESET.content?.privacy?.termsLink?.text ?? 'Terms of Service',
        url: CORNER_PRESET.content?.privacy?.termsLink?.url ?? `${siteConfig.baseLinks.terms}`,
        ...CORNER_PRESET.content?.privacy?.termsLink,
        ...customContent?.privacy?.termsLink
      }
    }
  }

  const renderForm = ({ state, handlers }: RenderProps): JSX.Element => {
    const { email, error, isSubmitting, isSuccess } = state
    const { handleChange, handleSubmit, handleBlur } = handlers

    const positionClasses = {
      'bottom-right': 'right-0 bottom-0',
      'bottom-left': 'left-0 bottom-0'
    }

    const offset = theme.offset ?? CORNER_PRESET.theme?.offset

    return (
      <div className={cn(
        "fixed",
        positionClasses[position],
        theme.className?.container,
        "z-40",
        className
      )}
      style={{
        bottom: offset?.bottom ?? '2rem',
        right: position === 'bottom-right' ? (offset?.right ?? '2rem') : undefined,
        left: position === 'bottom-left' ? (offset?.left ?? '2rem') : undefined
      }}>
        <AnimatePresence mode="wait">
          {isExpanded ? (
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className={cn(
                "overflow-hidden",
                theme.className?.form
              )}
            >
              <div className="p-6">
                {/* Close Button */}
                <button
                  onClick={() => setIsExpanded(false)}
                  className="absolute right-4 top-4 rounded-full p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700 
                           dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-300"
                >
                  <RiCloseLine className="h-5 w-5" />
                </button>

                {content.title && (
                  <h3 className={cn(
                    "text-lg font-semibold text-gray-900 dark:text-white",
                    content.title.className
                  )}>
                    {content.title.text}
                  </h3>
                )}

                {content.description && (
                  <p className={cn(
                    "mt-2 text-sm text-gray-600 dark:text-gray-400",
                    content.description.className
                  )}>
                    {content.description.text}
                  </p>
                )}

                <form onSubmit={handleSubmit} className="mt-4">
                  <div className="relative">
                    <input
                      type="email"
                      value={email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder={content.input.placeholder}
                      className={cn(
                        "block w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-gray-900 placeholder-gray-500",
                        "focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500",
                        "dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:placeholder-gray-400",
                        "dark:focus:border-indigo-500 dark:focus:ring-indigo-500",
                        error && "border-red-300 focus:border-red-500 focus:ring-red-500",
                        content.input.className
                      )}
                    />
                    {error && (
                      <p className="mt-2 text-sm text-red-600 dark:text-red-500">{error}</p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={cn(
                      "mt-3 flex w-full items-center justify-center rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white",
                      "hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2",
                      "disabled:cursor-not-allowed disabled:opacity-60",
                      "dark:focus:ring-offset-gray-900",
                      content.button.className
                    )}
                  >
                    {isSubmitting ? content.button.loadingText : content.button.text}
                  </button>

                  {(error ?? isSuccess) && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mt-4"
                    >
                      {error && (
                        <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
                          <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/20">
                            <span className="h-2 w-2 rounded-full bg-red-600 dark:bg-red-400" />
                          </span>
                          {error}
                        </div>
                      )}
                      {isSuccess && (
                        <div className="rounded-lg bg-green-50 dark:bg-green-900/20 px-3 py-2 border border-green-100 dark:border-green-800/30">
                          <p className="text-sm font-medium text-green-800 dark:text-green-300">
                            {content.messages.success}
                          </p>
                        </div>
                      )}
                    </motion.div>
                  )}

                  {content.privacy.enabled && (
                    <p className={cn(
                      "mt-3 text-xs text-gray-500 dark:text-gray-400",
                      content.privacy.className
                    )}>
                      {content.privacy.text}{' '}
                      <a 
                        href={content.privacy.privacyLink.url}
                        className="font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400"
                      >
                        {content.privacy.privacyLink.text}
                      </a>
                      {content.privacy.termsLink && (
                        <>
                          {' and '}
                          <a 
                            href={content.privacy.termsLink.url}
                            className="font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400"
                          >
                            {content.privacy.termsLink.text}
                          </a>
                        </>
                      )}
                    </p>
                  )}
                </form>
              </div>
            </motion.div>
          ) : (
            <motion.button
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              onClick={() => setIsExpanded(true)}
              className={cn(
                "flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white shadow-lg",
                "hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2",
                "dark:focus:ring-offset-gray-900",
                theme.className?.button
              )}
            >
              {content.floatingButton?.icon ?? <RiMailLine className="h-5 w-5" />}
              <span>{content.floatingButton?.text ?? content.button.text}</span>
            </motion.button>
          )}
        </AnimatePresence>
      </div>
    )
  }

  return (
    <FormErrorBoundary>
      <BaseEmailCaptureForm
        ref={ref}
        theme={theme}
        content={content}
        onSubmit={onSubmit}
        {...props}
      >
        {renderForm}
      </BaseEmailCaptureForm>
    </FormErrorBoundary>
  )
})

CornerEmailCapture.displayName = 'CornerEmailCapture' 